/**
 * FollowUpList Component - Share Load-first follow-ups with tab navigation
 */

import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useFollowUps,
  useCompleteFollowUp,
  useDeleteFollowUp,
  useRescheduleFollowUp,
  useUpdateFollowUp,
} from "../hooks/useFollowUps.js";
import FollowUpGroupedList from "./FollowUpGroupedList.jsx";
import { groupByTime } from "../utils/groupByTime.js";
import { logError } from "@/shared/utils/logger.js";
import { addDays } from "date-fns";

/**
 * Action buckets configuration
 */
const ACTION_BUCKETS = {
  needsAction: {
    label: "Needs Action",
    statuses: ["overdue", "due"],
    default: true,
  },
  scheduled: {
    label: "Scheduled",
    statuses: ["pending"],
  },
  completed: {
    label: "Completed",
    statuses: ["completed"],
  },
  archived: {
    label: "Archived",
    statuses: ["cancelled"],
  },
};

/**
 * FollowUpList Component
 */
const FollowUpList = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("needsAction");
  const [filters, setFilters] = useState({
    entityType: "quote", // Default: Share Load
    timeFilter: "today", // Default: Today + Overdue
    search: "",
  });

  // Bulk selection state
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [busyById, setBusyById] = useState({});

  // Bulk reschedule dialog state
  const [bulkRescheduleOpen, setBulkRescheduleOpen] = useState(false);
  const [bulkRescheduleDueDate, setBulkRescheduleDueDate] = useState("");

  const activeBucket = ACTION_BUCKETS[activeTab];

  // Build status filter from active tab
  const statusFilter = activeBucket.statuses.join(",");

  // Get follow-ups with tab-based status + filters
  const { data, isLoading, error, refetch } = useFollowUps({
    ...filters,
    status: statusFilter,
  });

  const completeMutation = useCompleteFollowUp();
  const deleteMutation = useDeleteFollowUp();
  const rescheduleMutation = useRescheduleFollowUp();
  const updateMutation = useUpdateFollowUp();

  const followUps = data?.followUps || [];

  // Count for badge (only on Needs Action)
  const needsActionCount = useMemo(() => {
    if (activeTab !== "needsAction") return 0;
    return followUps.filter((fu) => ["overdue", "due"].includes(fu.status))
      .length;
  }, [followUps, activeTab]);

  // Group by time
  const groupedFollowUps = useMemo(() => {
    return groupByTime(followUps, activeTab === "completed");
  }, [followUps, activeTab]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      // Store empty string for "All" selections (will be filtered out in service)
      [field]: value,
    }));
  };

  // Helper to get follow-up ID
  const getId = (fu) => fu?.id || fu?._id;

  // Helper to set busy state
  const setBusy = (id, isBusy) => {
    setBusyById((prev) => ({ ...prev, [id]: isBusy }));
  };

  // Toggle selection
  const toggleSelected = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Clear selection
  const clearSelection = () => setSelectedIds([]);

  // Bulk operation helper
  const bulkRun = async (ids, fnPerId) => {
    const results = await Promise.allSettled(ids.map(fnPerId));
    const ok = results.filter((r) => r.status === "fulfilled").length;
    const fail = results.length - ok;
    return { ok, fail };
  };

  // Bulk complete handler
  const handleBulkComplete = async () => {
    if (selectedIds.length === 0) return;
    if (
      !window.confirm(`Mark ${selectedIds.length} follow-up(s) as completed?`)
    )
      return;

    const { ok, fail } = await bulkRun(selectedIds, (id) =>
      completeMutation.mutateAsync({ followUpId: id, followUpNotes: "" })
    );

    alert(`Completed: ${ok}, Failed: ${fail}`);
    clearSelection();
    refetch();
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} follow-up(s)?`)) return;

    const { ok, fail } = await bulkRun(selectedIds, (id) =>
      deleteMutation.mutateAsync(id)
    );

    alert(`Deleted: ${ok}, Failed: ${fail}`);
    clearSelection();
    refetch();
  };

  // Bulk archive handler
  const handleBulkArchive = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Archive ${selectedIds.length} follow-up(s)?`)) return;

    const { ok, fail } = await bulkRun(selectedIds, (id) =>
      updateMutation.mutateAsync({
        followUpId: id,
        updateData: { status: "cancelled" },
      })
    );

    alert(`Archived: ${ok}, Failed: ${fail}`);
    clearSelection();
    refetch();
  };

  // Bulk reschedule handler
  const handleBulkReschedule = async () => {
    if (selectedIds.length === 0) return;
    if (!bulkRescheduleDueDate) return;

    const dueDate = new Date(bulkRescheduleDueDate).toISOString();

    const { ok, fail } = await bulkRun(selectedIds, (id) =>
      rescheduleMutation.mutateAsync({ followUpId: id, dueDate })
    );

    alert(`Rescheduled: ${ok}, Failed: ${fail}`);
    setBulkRescheduleOpen(false);
    setBulkRescheduleDueDate("");
    clearSelection();
    refetch();
  };

  // Single follow-up action handler (now with async loading states)
  const handleAction = async (followUp, action) => {
    const followUpId = getId(followUp);

    if (!followUpId) {
      logError("Follow-up missing ID", {
        followUp,
        action,
        availableKeys: Object.keys(followUp || {}),
      });
      return;
    }

    // Allow selection toggle if in bulk mode
    if (bulkSelectMode && action === "primary") {
      toggleSelected(followUpId);
      return;
    }

    try {
      // Mark busy for async actions
      const needsBusy = ["complete", "delete", "snooze", "archive"].includes(
        action
      );
      if (needsBusy) setBusy(followUpId, true);

      switch (action) {
        case "primary":
          navigate(`/followups/${followUpId}`);
          break;

        case "complete":
          if (window.confirm("Mark this follow-up as completed?")) {
            await completeMutation.mutateAsync({
              followUpId,
              followUpNotes: "",
            });
            refetch();
          }
          break;

        case "edit":
          navigate(`/followups/${followUpId}/edit`);
          break;

        case "delete":
          if (
            window.confirm("Are you sure you want to delete this follow-up?")
          ) {
            await deleteMutation.mutateAsync(followUpId);
            refetch();
          }
          break;

        case "archive":
          if (window.confirm("Archive this follow-up?")) {
            await updateMutation.mutateAsync({
              followUpId,
              updateData: { status: "cancelled" },
            });
            refetch();
          }
          break;

        case "snooze": {
          // Snooze = reschedule +1 day (keeps time if dueDate exists)
          const base = followUp?.dueDate
            ? new Date(followUp.dueDate)
            : new Date();
          const next = addDays(base, 1).toISOString();
          await rescheduleMutation.mutateAsync({
            followUpId,
            dueDate: next,
          });
          refetch();
          break;
        }

        default:
          navigate(`/followups/${followUpId}`);
      }
    } catch (error) {
      logError("Error handling follow-up action", {
        action,
        followUpId,
        error: error.message,
        stack: error.stack,
      });
      alert(`Failed to ${action} follow-up: ${error.message}`);
    } finally {
      setBusy(followUpId, false);
    }
  };

  return (
    <Box>
      {/* Command Zone */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5">Follow-ups</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant={bulkSelectMode ? "contained" : "outlined"}
            onClick={() => {
              setBulkSelectMode((v) => !v);
              if (bulkSelectMode) clearSelection();
            }}
          >
            {bulkSelectMode ? "Cancel Selection" : "Select Multiple"}
          </Button>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate("/followups/new")}
        >
          Create Follow-up
        </Button>
      </Box>
      </Box>

      {/* Bulk Actions Bar */}
      {bulkSelectMode && selectedIds.length > 0 && (
        <Box
          sx={(theme) => ({
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            p: 2,
            borderRadius: 1,
            backgroundColor: theme.palette.action.selected,
            gap: 1,
            flexWrap: "wrap",
          })}
        >
          <Typography variant="body2">{selectedIds.length} selected</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleBulkComplete}
            >
              Complete Selected
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setBulkRescheduleOpen(true)}
            >
              Reschedule Selected
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={handleBulkArchive}
            >
              Archive Selected
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={handleBulkDelete}
            >
              Delete Selected
            </Button>
            <Button size="small" variant="text" onClick={clearSelection}>
              Clear
            </Button>
          </Box>
        </Box>
      )}

      {/* Tab Navigation */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: activeTab === "needsAction" ? 600 : 400,
            },
          }}
        >
          <Tab
            label={
              <Badge badgeContent={needsActionCount} color="error" max={99}>
                Needs Action
              </Badge>
            }
            value="needsAction"
          />
          <Tab label="Scheduled" value="scheduled" />
          <Tab label="Completed" value="completed" />
          <Tab label="Archived" value="archived" />
        </Tabs>
      </Box>

      {/* Secondary Filters (Lightweight) */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Time</InputLabel>
          <Select
            value={filters.timeFilter ?? ""}
            onChange={(e) => handleFilterChange("timeFilter", e.target.value)}
            label="Time"
          >
            <MenuItem value="today">Today + Overdue</MenuItem>
            <MenuItem value="tomorrow">Tomorrow</MenuItem>
            <MenuItem value="thisWeek">This Week</MenuItem>
            <MenuItem value="">All Time</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={filters.entityType ?? ""}
            onChange={(e) => handleFilterChange("entityType", e.target.value)}
            label="Type"
          >
            <MenuItem value="order">Share Load</MenuItem>
            <MenuItem value="quote">Quote</MenuItem>
            <MenuItem value="customer">Customer</MenuItem>
            <MenuItem value="invoice">Invoice</MenuItem>
            <MenuItem value="lead">Lead</MenuItem>
            <MenuItem value="">All Types</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message || "Failed to load follow-ups"}
        </Alert>
      )}

      {/* Grouped List */}
      {!isLoading && !error && (
        <FollowUpGroupedList
          grouped={groupedFollowUps}
          mode={activeTab}
          onAction={handleAction}
          selectMode={bulkSelectMode}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelected}
          busyById={busyById}
        />
      )}

      {/* Bulk Reschedule Dialog */}
      <Dialog
        open={bulkRescheduleOpen}
        onClose={() => setBulkRescheduleOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Reschedule {selectedIds.length} follow-up(s)
        </DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="New due date/time"
            type="datetime-local"
            fullWidth
            value={bulkRescheduleDueDate}
            onChange={(e) => setBulkRescheduleDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkRescheduleOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleBulkReschedule}
            disabled={!bulkRescheduleDueDate}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FollowUpList;
